import type { ProgressData, ProgressOut } from '@dcs/contracts';

/**
 * What the sync needs from the server. `@dcs/api` implements it (the app wires it in; platform must
 * not import the API client, plan §2.2). Any thrown error is treated as "could not reach the
 * server": the data stays dirty and is retried on the next tick, except a 401: an error whose
 * `status` is `401` (`ApiClientError` of `@dcs/api` is) stops the sync until the next sign-in.
 */
export interface ProgressTransport {
  /** Progress of every class of the signed-in user. */
  list(): Promise<readonly ProgressOut[]>;
  /** One class; `null` when the user has none yet. */
  get(licenseClass: string): Promise<ProgressOut | null>;
  /**
   * `PUT`. `baseUpdatedAt` is absent when the caller saw no row. `keepalive` asks for a request that
   * may outlive the page (used when the page is hidden). A stale base resolves to `conflict`.
   */
  put(
    licenseClass: string,
    body: { data: ProgressData; baseUpdatedAt?: string },
    options?: { keepalive?: boolean },
  ): Promise<{ status: 'saved'; progress: ProgressOut } | { status: 'conflict' }>;
  /**
   * Cancels every request in flight (they reject). Called when the sync is disposed (sign-out,
   * unmount), so no answer of the old session is processed after it.
   */
  abort?(): void;
}

/** Timers are injected so the debounce is testable and the package needs no DOM lib. */
export interface SyncTimers {
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

/** A 401 from the transport (the session is gone): the sync stops instead of retrying. */
export function isUnauthorized(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 401
  );
}
